import { CollectionConfig } from "@rebasepro/types";
import companiesCollection from "./companies";
import talentsCollection from "./talents";
import jobsCollection from "./jobs";
import { PRESENTATION_STATE_LABEL } from "../presentations";

/**
 * A person the team chose to present to an employer (drizzle/0069).
 *
 * The sanctioned channel between the candidate pool and a company: since 0060 an
 * employer reads the people who applied to it, and — through this table — the
 * people a recruiter presented to it, and nobody else. Every row is an act
 * somebody on the team took; nothing writes one on its own.
 *
 * Worked from the covers, not from here: the company's cover lists who was
 * presented and what they answered, the candidate's cover lists where they were
 * presented, and both carry the "Presentar" action. This raw table stays out of
 * the drawer for the same reason `notes` does.
 */
const presentedCandidatesCollection: CollectionConfig = {
    name: "Candidatos presentados",
    singularName: "Candidato presentado",
    slug: "presented-candidates",
    table: "presented_candidates",
    securityRules: [
        {
            // The company reads the presentations addressed to it.
            name: "company_read_own_presentations",
            operation: "select",
            using: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = presented_candidates.company_id)"
        },
        {
            // …and answers them: "Lo hemos contactado" / "No nos encaja".
            //
            // RLS decides which rows a member may write, never which columns.
            // The trigger `presented_candidates_guard` (drizzle/0069) is what
            // limits the write to `employer_state` and `decline_reason` —
            // without it a member could point `talent_id` at anybody and
            // `company_read_presented_candidates` on talents would open that
            // person's row. It also stamps `employer_state_at` from the server
            // clock. `admin.readOnly` below is a form setting and guards
            // nothing on its own.
            name: "company_answer_own_presentations",
            operation: "update",
            using: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = presented_candidates.company_id)",
            withCheck: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = presented_candidates.company_id)"
        },
        {
            // Only the team presents. No INSERT for a company (it would be
            // choosing its own candidates out of a pool it may not browse), and
            // none for a candidate. The candidate learns where they were
            // presented from `functions/my-presentations`, which returns the
            // company and the date and not the note or the answer — both of
            // which were written for somebody else.
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        talent_id: {
            name: "Candidato",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => talentsCollection,
                localKey: "talent_id"
            },
            validation: { required: true },
            admin: { readOnly: true }
        },
        company_id: {
            name: "Empresa",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => companiesCollection,
                localKey: "company_id"
            },
            validation: { required: true },
            admin: { readOnly: true }
        },
        job_id: {
            name: "Para la vacante",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => jobsCollection,
                localKey: "job_id"
            },
            description: "Vacía cuando la presentamos en general, sin una vacante concreta.",
            admin: { readOnly: true }
        },
        note_for_employer: {
            name: "Lo que le dijimos a la empresa",
            type: "string",
            validation: { required: true },
            description: "La empresa lo lee tal cual en su panel. La persona candidata no lo ve.",
            admin: { multiline: true }
        },
        presented_by: {
            name: "Lo presentó",
            type: "string",
            userSelect: true,
            description: "La cuenta del equipo que la presentó. La pone la base de datos, no el formulario.",
            admin: { readOnly: true }
        },
        presented_at: {
            name: "Presentado",
            type: "date",
            autoValue: "on_create",
            admin: { readOnly: true }
        },
        employer_state: {
            name: "Qué hizo la empresa",
            type: "string",
            enum: [
                { id: "new", label: PRESENTATION_STATE_LABEL.new, color: "grayLight" },
                { id: "seen", label: PRESENTATION_STATE_LABEL.seen, color: "blue" },
                { id: "contacted", label: PRESENTATION_STATE_LABEL.contacted, color: "green" },
                { id: "declined", label: PRESENTATION_STATE_LABEL.declined, color: "orange" }
            ],
            defaultValue: "new",
            description: "Lo marca la empresa desde su panel. «Han abierto su panel» sólo dice que lo tuvieron en pantalla.",
            admin: { readOnly: true }
        },
        employer_state_at: {
            name: "Desde",
            type: "date",
            description: "Cuándo cambió por última vez, con el reloj del servidor.",
            admin: { readOnly: true }
        },
        decline_reason: {
            name: "Por qué no les encaja",
            type: "string",
            description: "La línea que escribe la empresa al descartar. Es para el equipo: la persona candidata no la ve.",
            admin: { readOnly: true }
        }
    },
    admin: {
        group: "Empresas",
        icon: "UserCheck",
        hideFromNavigation: true,
        sort: ["presented_at", "desc"],
        listProperties: ["talent_id", "company_id", "employer_state", "presented_at"],
        display: {
            title: "talent_id",
            subtitle: "company_id",
            status: "employer_state",
            date: "presented_at"
        },
        previewProperties: ["talent_id", "company_id", "employer_state"]
    }
};

export default presentedCandidatesCollection;
